import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGift } from '@fortawesome/pro-regular-svg-icons';

/**
 * The three gift covers, at the size the legacy page asked imgix for.
 *
 * Kept as the absolute urls the legacy component used rather than rebuilt here: they are
 * already sized and already on the image CDN, so re-deriving them would be a second copy
 * of a transform to keep in step with nothing.
 *
 * They are signed imgix urls — the `s=` is an HMAC over the path — so they cannot be
 * re-pointed at another host without re-signing, and `img.discountmags.com` is the one
 * imgix domain every site's images go through (`config/services/images.php` registers it
 * for all of them), not this brand's. It is still the only place in `shared/` that names
 * a host, which is the price of keeping the legacy component's exact artwork; the day a
 * second brand turns `showGiftingPromo` on with cards of its own, the list becomes a prop.
 */
const IMAGES = [
  {
    src: 'https://img.discountmags.com/https%3A%2F%2Fwww.discountmags.com%2Fshopimages%2Fproducts%2Fthumbnails%2Fmerry-christmas-2-rgb.jpg?auto=format%2Ccompress&cs=strip&h=310&w=456&s=494ce61fd54f915577bf743c24aa2d30',
    alt: 'A Merry Christmas gift announcement card',
  },
  {
    src: 'https://img.discountmags.com/https%3A%2F%2Fwww.discountmags.com%2Fshopimages%2Fproducts%2Fthumbnails%2Fhappy-new-year.jpg?auto=format%2Ccompress&cs=strip&h=310&w=456&s=037f0f840100528d2f060cc1bd310841',
    alt: 'A Happy New Year gift announcement card',
  },
  {
    src: 'https://img.discountmags.com/https%3A%2F%2Fwww.discountmags.com%2Fshopimages%2Fproducts%2Fthumbnails%2Ffront-a-gift-for-you.png?auto=format%2Ccompress&cs=strip&h=310&w=456&s=5db2264b9bb601f06b373e47fa2b67db',
    alt: 'A gift for you announcement card',
  },
];

/**
 * "Did you know you can gift magazines to your friends?"
 *
 * A port of the legacy `mega_sale/categories/gifting.js` (since retired), which the page carried for
 * years and which this rewrite had dropped. It sits after the first section, where the
 * legacy loop put it, and it is the only thing on the page that explains that the gift
 * option exists at all — the tile's own gift affordance is a checkbox in the cart and the
 * upsell after an add is a single line of copy.
 *
 * Copy and images are the legacy ones exactly. What is new is the layout: three fixed
 * 150px covers in a row do not fit a 390px phone, so they are a grid that shares the width
 * it has.
 */
export default function GiftingPromo() {
  return (
    <aside className="my-6 rounded-lg border-[1.5px] border-slate-200 p-4 md:p-6 text-center">
      <FontAwesomeIcon icon={faGift} className="text-3xl md:text-4xl text-primary-600" />
      <h2 className="mt-2 md:mt-3 mb-0 text-lg font-bold text-slate-900">
        Did you know you can gift magazines to your friends?
      </h2>
      <p className="mx-auto mt-2 mb-3 max-w-[650px] text-xs font-medium text-slate-600">
        Just select &ldquo;this will be a gift&rdquo; after you make a selection and you can send specific
        magazines from your cart to a separate mailing address and even add a personalized gift note.
      </p>
      <ul className="list-none m-0 p-0 flex flex-row items-center justify-center gap-2">
        {IMAGES.map((image) => (
          <li key={image.src} className="min-w-0 max-w-[150px] flex-1">
            <img
              src={image.src}
              alt={image.alt}
              width={456}
              height={310}
              loading="lazy"
              className="w-full h-auto rounded-sm border-[1.5px] border-slate-100"
            />
          </li>
        ))}
      </ul>
    </aside>
  );
}
